import {
  Box,
  Button,
  Center,
  Heading,
  Spinner,
  Text,
  useDisclosure
} from '@chakra-ui/react'
import { useAdminUser } from '@olagg/api-hooks'
import { OlaggModule, PermissionEnum } from '@olagg/db-types'
import type { User } from '@olagg/db-types'
import { useAuthStore } from '@olagg/store'
import { useParams } from 'react-router-dom'
import UserForm from '../components/Forms/UserForm'
import DeleteUserModal from '../components/Modals/DeleteUserModal'
import useRoles from '../features/Roles/hooks/useRoles'

interface IProps {
  mode: 'edit' | 'view'
}

const isRegularUser = (user: User) =>
  (user.roles ?? []).every(role => role === 'USER')

interface DangerZoneProps {
  user: User
  onDeleted: () => void | Promise<void>
}

const DangerZone = ({ user, onDeleted }: DangerZoneProps) => {
  const { me } = useAuthStore()
  const { hasPermission } = useRoles()
  const { isOpen, onOpen, onClose } = useDisclosure()

  const canDelete = hasPermission(OlaggModule.User, PermissionEnum.DELETE)
  const isSelf = me?.id === user.id
  const isAdminTarget = !isRegularUser(user)

  let blockedReason: string | null = null
  if (!canDelete) blockedReason = 'No tienes permiso para eliminar usuarios.'
  else if (isSelf) blockedReason = 'No puedes eliminar tu propia cuenta.'
  else if (isAdminTarget) blockedReason = 'No se pueden eliminar usuarios con roles de administración.'

  return (
    <Box
      w={{ base: '70vw', lg: '50vw' }}
      mt={10}
      p={5}
      border="1px solid"
      borderColor="red.400"
      borderRadius="md"
      color="white"
    >
      <Heading size="md" color="red.300" mb={2}>
        Zona de peligro
      </Heading>
      <Text fontSize="sm" mb={4}>
        Eliminar la cuenta borra al usuario y todos sus datos de forma permanente.
      </Text>
      <Button
        colorScheme="red"
        onClick={onOpen}
        isDisabled={blockedReason !== null}
      >
        Eliminar cuenta
      </Button>
      {blockedReason && (
        <Text fontSize="xs" color="gray.400" mt={2}>
          {blockedReason}
        </Text>
      )}
      <DeleteUserModal
        isOpen={isOpen}
        onClose={onClose}
        user={user}
        onDeleted={onDeleted}
      />
    </Box>
  )
}

// TODO: Use the param to get the mode. By default will be 'view'
const ManageUser = () => {
  const params = useParams()

  const { data, isLoading, mutate } = useAdminUser(params?.id || '')

  if (isLoading) return <Spinner />

  // Con error (por ejemplo 404 de un usuario ya borrado) data es undefined.
  const user = data?.user

  // Al borrar, vaciamos la caché SWR de este usuario sin revalidar para que
  // volver atrás en el navegador no muestre una cuenta que ya no existe.
  const handleDeleted = async () => {
    await mutate(undefined, false)
  }

  if (user)
    return (
      <>
        <UserForm
          mode={
            ['view', 'edit'].includes(params.mode as string)
              ? (params.mode as 'view' | 'edit')
              : 'view'
          }
          user={user}
        />
        <Center w="full">
          <DangerZone user={user} onDeleted={handleDeleted} />
        </Center>
      </>
    )

  return (
    <Center h="80vh" color="white">
      <Text>User not found</Text>
    </Center>
  )
}

export default ManageUser
